import { motion, useMotionValue, useSpring, useTransform, type MotionValue } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export type DockItem = { title: string; icon: ReactNode; href: string };

export function FloatingDock({ items, className }: { items: DockItem[]; className?: string }) {
  const mouseX = useMotionValue(Infinity);
  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.pageX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={cn(
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-50",
        "flex items-end gap-3 px-4 py-3 rounded-full bg-bg-2/80 backdrop-blur-xl border border-line",
        "shadow-[0_8px_40px_-8px_rgba(0,0,0,0.6)]",
        className
      )}
    >
      {items.map((item) => <DockIcon key={item.title} mouseX={mouseX} item={item} />)}
    </motion.div>
  );
}

function DockIcon({ mouseX, item }: { mouseX: MotionValue<number>; item: DockItem }) {
  const ref = useRef<HTMLDivElement>(null);
  const distance = useTransform(mouseX, (val: number) => {
    const b = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - b.x - b.width / 2;
  });
  const widthSync = useTransform(distance, [-150, 0, 150], [40, 70, 40]);
  const width = useSpring(widthSync, { mass: 0.1, stiffness: 150, damping: 12 });
  const [hovered, setHovered] = useState(false);

  return (
    <Link to={item.href} aria-label={item.title}>
      <motion.div
        ref={ref}
        style={{ width, height: width }}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        className="aspect-square rounded-full flex items-center justify-center bg-bg-3 border border-line text-fg-2 hover:text-accent transition-colors relative"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 10 }}
          className="absolute -top-9 px-2 py-1 rounded-md bg-bg-3 border border-line text-xs whitespace-pre pointer-events-none"
        >
          {item.title}
        </motion.div>
        {item.icon}
      </motion.div>
    </Link>
  );
}
